// About page — soft, editorial, on-brand. Bilingual via the i18n catalog.
// Lives at /about. Replaces the previous stub which delegated to <LegalPage>.

import { Link } from "react-router";
import {
  Clock3,
  Heart,
  MapPin,
  PlayCircle,
  LockKeyhole,
  Gift,
  Smartphone,
  Sparkles,
} from "lucide-react";
import { useLocale } from "../locale/locale-context.tsx";

export function AboutPage() {
  const { t } = useLocale();

  return (
    <div className="bg-[var(--brand-paper)] text-[var(--brand-ink)]">
      <Hero />
      <WhyShortForm />
      <Bangkok />
      <Pillars />
      <ContactFooter />
    </div>
  );

  function Hero() {
    return (
      <section className="relative isolate overflow-hidden bg-[var(--brand-pink)]">
        {/* Soft corner blooms — mirrors landing + legal pages for visual
            continuity across the customer surface. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background:radial-gradient(48rem_32rem_at_85%_0%,rgba(233,75,122,0.22),transparent_60%),radial-gradient(40rem_28rem_at_10%_100%,rgba(252,238,243,0.9),transparent_65%)]"
        />
        <div className="relative mx-auto max-w-5xl px-6 pb-20 pt-16 sm:px-8 sm:pb-28 sm:pt-24">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[var(--brand-rose)] backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {t("about.eyebrow")}
          </span>
          <h1 className="mt-6 font-display text-5xl leading-[0.95] tracking-tight text-[var(--brand-ink)] sm:text-6xl lg:text-7xl">
            {t("about.hero.title")}
          </h1>
          <p className="mt-5 max-w-xl text-lg text-[var(--brand-ink)]/80">
            {t("about.hero.tagline")}
          </p>
          <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--brand-ink)]/85">
            {t("about.hero.body")}
          </p>
        </div>
      </section>
    );
  }

  function WhyShortForm() {
    const points = [
      { Icon: Clock3, title: t("about.why.point_1.title"), body: t("about.why.point_1.body") },
      { Icon: Heart, title: t("about.why.point_2.title"), body: t("about.why.point_2.body") },
      { Icon: PlayCircle, title: t("about.why.point_3.title"), body: t("about.why.point_3.body") },
    ];
    return (
      <section className="mx-auto max-w-5xl px-6 py-20 sm:px-8 sm:py-24">
        <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
          {t("about.why.title")}
        </h2>
        <p className="mt-4 max-w-2xl text-base leading-7 text-[var(--brand-ink)]/75">
          {t("about.why.body")}
        </p>
        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {points.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="rounded-3xl border border-[var(--brand-border)] bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.03)]"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--brand-pink-soft)] text-[var(--brand-rose)]">
                <Icon className="h-5 w-5" aria-hidden />
              </span>
              <p className="mt-4 font-heading text-lg font-semibold text-[var(--brand-ink)]">
                {title}
              </p>
              <p className="mt-1 text-sm text-[var(--brand-ink)]/70">{body}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  function Bangkok() {
    return (
      <section className="bg-[var(--brand-pink-soft)]">
        <div className="mx-auto grid max-w-5xl gap-8 px-6 py-20 sm:grid-cols-[auto_1fr] sm:gap-12 sm:px-8 sm:py-24">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--brand-rose)] text-white shadow-sm">
            <MapPin className="h-9 w-9" aria-hidden />
          </div>
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              {t("about.bangkok.title")}
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-7 text-[var(--brand-ink)]/85">
              {t("about.bangkok.body")}
            </p>
          </div>
        </div>
      </section>
    );
  }

  function Pillars() {
    // Mirrors the landing pillars but extended with a 4th tile (anywhere) so
    // About reads as the canonical, settled product description.
    const items = [
      {
        Icon: PlayCircle,
        title: t("about.pillars.free"),
        sub: t("about.pillars.free.sub"),
      },
      {
        Icon: LockKeyhole,
        title: t("about.pillars.unlock"),
        sub: t("about.pillars.unlock.sub"),
      },
      { Icon: Gift, title: t("about.pillars.bundle"), sub: t("about.pillars.bundle.sub") },
      {
        Icon: Smartphone,
        title: t("about.pillars.anywhere"),
        sub: t("about.pillars.anywhere.sub"),
      },
    ];
    return (
      <section className="mx-auto max-w-5xl px-6 py-20 sm:px-8 sm:py-24">
        <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">
          {t("about.pillars.title")}
        </h2>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ Icon, title, sub }) => (
            <div
              key={title}
              className="flex items-start gap-4 rounded-2xl border border-[var(--brand-border)] bg-white p-5"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--brand-ink)]">
                <Icon className="h-5 w-5 text-white" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="font-heading text-base font-bold leading-tight text-[var(--brand-ink)]">
                  {title}
                </p>
                <p className="mt-1 text-sm text-[var(--brand-ink)]/70">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  function ContactFooter() {
    return (
      <section className="bg-[var(--brand-ink)] text-[var(--brand-paper)]">
        <div className="mx-auto flex max-w-5xl flex-col items-start gap-6 px-6 py-16 sm:px-8 sm:py-20 md:flex-row md:items-center md:justify-between">
          <div className="max-w-md">
            <h2 className="font-heading text-2xl font-bold sm:text-3xl">
              {t("about.contact.title")}
            </h2>
            <p className="mt-2 text-sm text-white/70">{t("about.contact.body")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/contact-us"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[var(--brand-rose)] px-6 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              {t("about.contact.cta_contact")}
            </Link>
            <Link
              to="/faq"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/30 bg-transparent px-6 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              {t("about.contact.cta_faq")}
            </Link>
          </div>
        </div>
      </section>
    );
  }
}
